import { captureException } from "@sentry/browser";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { hasPendingChunkReload, reloadPage } from "common/utils/chunkReloadGuard";
import { isChunkLoadError } from "common/utils/isChunkLoadError";
import { useEffect, useRef, useState } from "react";
import Button from "ui/buttons/Button";
import IconWarning from "ui/icons/IconWarning";
import styles from "./RouterErrorComponent.module.scss";

/** Reports route failures and offers recovery when no chunk reload is pending. */
export default function RouterErrorComponent({ error }: ErrorComponentProps) {
  const reportedError = useRef<Error | null>(null);
  const isChunkLoad = isChunkLoadError(error);
  const [isReloading, setIsReloading] = useState(isChunkLoad);

  useEffect(() => {
    // defaultOnCatch runs after the fallback renders, before this effect.
    const reloadPending = isChunkLoad && hasPendingChunkReload();
    setIsReloading(reloadPending);
    if (reloadPending || reportedError.current === error) {
      return;
    }
    // StrictMode invokes mount effects twice.
    reportedError.current = error;
    captureException(error);
  }, [error, isChunkLoad]);

  if (isReloading) {
    return null;
  }

  return (
    <div className={styles.wrapper} role="alert">
      <div className={styles.panel}>
        <div className={styles.heading}>
          <IconWarning size={20} className={styles.icon} aria-hidden="true" />
          <h1 className={styles.title}>Something went wrong.</h1>
        </div>
        <p className={styles.body}>
          This page hit an unexpected error and cannot continue. Our engineers have been notified. Reloading usually
          clears it.
        </p>
        <Button btnSize="large" onClick={reloadPage} dataTestId="router-error-reload">
          Reload page
        </Button>
        {/* Keep diagnostics visible for screenshots. */}
        {!!error.message && <pre className={styles.message}>{error.message}</pre>}
      </div>
    </div>
  );
}
